/* vl-gift — keeps campaign gift lines (the free pillows a bed brings) in step
   with their bed in the cart.
   vl-main-product.js adds a gift line together with the bed and gives it two
   hidden properties: _vl_gift_for (the bed variant id) and _vl_gift_per
   (pillows per bed). Whenever the cart drawer or the cart page changes a
   quantity, the gift lines of a bed are set to bed quantity × pillows per
   bed, and a gift whose bed has left the cart is removed. A gift line the
   shopper deletes on purpose stays deleted.
   2026-09-27: a gift line the shopper RAISES means "I want more of this
   pillow". Shopify splits such a line by itself (one unit free through the
   campaign discount, the other paid — two lines with the same properties),
   and until now each half was judged alone: the paid half kept the "Hediye"
   label, doubled with the bed's quantity and vanished with the bed. Now the
   halves of one gift are judged as a group: the free units stay the gift,
   the surplus becomes an ordinary paid line of the same pillow (no gift
   properties), which the shopper can raise or remove like any other item.
   Shopify gives the free unit to the NEWEST eligible line of the pillow, so
   a paid pillow added from its product page next to the gift took the free
   unit and the gift line became the charged one (the state Alina reported):
   whenever a plain line of the pillow holds the free unit, the gift line is
   re-added last and gets it back. Runs after every cart change (cart:update
   from the drawer / cart page, cart:reload after any add) and once on load
   when the page was rendered with a gift line in the cart (data-vl-gift-check
   on the script tag in layout/theme.liquid).
   2026-10-16: the campaign can be switched OFF (Theme settings → ViskoLove → "Gift pillow campaign is on" → data-vl-gift-off on
   the script tag): then every gift line is wanted at 0 and leaves the cart — a shopper who added a bed while the gift was on
   must not keep a "Hediye" pillow that admin would charge once the free-pillow discount is gone. Plain pillow lines stay. */
(function () {
  'use strict';
  if (window.vlGiftInit) return;
  window.vlGiftInit = true;

  var busy = false;
  var giftOff = false; // set from the script tag below; true = the campaign is over, gift lines are removed
  var again = false;
  var streak = 0; // passes in a row that had to change the cart; a run-away is stopped after three

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart change ' + r.status);
      return r.json();
    });
  }

  /* what the cart should look like: one list of cart API calls, in order */
  function plan(cart) {
    var beds = {}, freeElsewhere = {};
    cart.items.forEach(function (it) {
      if (!(it.properties && it.properties._vl_gift_for)) {
        beds[it.variant_id] = (beds[it.variant_id] || 0) + it.quantity;
        /* a plain line carrying a 100 % allocation = the campaign's free unit landed on it */
        (it.line_level_discount_allocations || []).forEach(function (a) {
          var d = a.discount_application || {};
          if (d.value_type === 'percentage' && parseFloat(d.value) >= 100 && a.amount > 0) freeElsewhere[it.variant_id] = true;
        });
      }
    });
    var groups = {}, order = [];
    cart.items.forEach(function (it) {
      var p = it.properties || {};
      if (!p._vl_gift_for) return;
      var k = it.variant_id + '|' + p._vl_gift_for + '|' + (p._vl_gift_per || '');
      if (!groups[k]) {
        groups[k] = { lines: [], total: 0, charged: 0, variant: it.variant_id, props: p, want: giftOff ? 0 : (beds[p._vl_gift_for] || 0) * (parseInt(p._vl_gift_per, 10) || 1) };
        order.push(k);
      }
      groups[k].lines.push(it);
      groups[k].total += it.quantity;
      groups[k].charged += it.final_line_price || 0;
    });
    var steps = [];
    order.forEach(function (k) {
      var g = groups[k];
      /* the gift is charged while a plain line of the same pillow got the free unit: rebuild below, so
         the gift line becomes the newest line again and takes the free unit back */
      var misplaced = g.total === g.want && g.want > 0 && g.charged > 0 && freeElsewhere[g.variant];
      if (g.total === g.want && !misplaced) return;
      if (g.total < g.want) {
        /* the bed count grew, or the shopper lowered the gift: the first line takes the difference */
        steps.push({ change: { id: g.lines[0].key, quantity: g.lines[0].quantity + (g.want - g.total) } });
        return;
      }
      /* more than the bed brings (or no bed left). All halves go in ONE update (line keys shift as soon as one
         half is gone — a second change.js answered 400). Shopify gives the free unit to the NEWEST eligible line of
         the pillow, so the surplus goes in first as a plain paid line and the gift is added back after it: the
         gift line stays the free one (HEDİYE), the plain line is priced like any pillow */
      var updates = {};
      g.lines.forEach(function (it) { updates[it.key] = 0; });
      steps.push({ update: { updates: updates } });
      if (g.want > 0) {
        if (g.total > g.want) steps.push({ add: { items: [{ id: g.variant, quantity: g.total - g.want }] } });
        steps.push({ add: { items: [{ id: g.variant, quantity: g.want, properties: g.props }] } });
      }
    });
    return steps;
  }

  function reconcile() {
    if (busy) { again = true; return; }
    busy = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var steps = plan(cart);
        if (!steps.length) { streak = 0; return false; }
        if (++streak > 3) { console.warn('vl-gift: cart keeps changing shape, giving up for now'); streak = 0; return false; }
        return steps.reduce(function (chain, step) {
          return chain.then(function () {
            if (step.update) return post('/cart/update.js', step.update);
            return step.change ? post('/cart/change.js', step.change) : post('/cart/add.js', step.add);
          });
        }, Promise.resolve()).then(function () { return true; });
      })
      .then(function (changed) {
        /* the theme's cart drawer and cart page re-render on cart:reload */
        if (changed) document.dispatchEvent(new CustomEvent('cart:reload'));
      })
      .catch(function (err) { console.error('vl-gift:', err); })
      .finally(function () {
        busy = false;
        if (again) { again = false; reconcile(); }
      });
  }

  /* the theme's cart components announce every quantity change and removal
     with a bubbling cart:update; the product pages, the sets and this script
     announce an add or a fix with cart:reload. A pass that finds the cart in
     order changes nothing, so reacting to our own reload is harmless. */
  document.addEventListener('cart:update', function () { reconcile(); });
  document.addEventListener('cart:reload', function () { reconcile(); });

  var me = document.currentScript;
  giftOff = !!(me && me.hasAttribute('data-vl-gift-off'));
  if (me && me.hasAttribute('data-vl-gift-check')) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcile);
    else reconcile();
  }
})();
